import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState } from 'react';
import {
  makeStyles, tokens, Spinner, Badge, Button,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  Field, Input, Select, Textarea,
  
} from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useT } from '../i18n';
import { NumberInput } from '../components/shared/NumberInput';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import { useRanchoLotes } from '../hooks/useRanchoLotes';
import { fetchModules } from '../api/modules.api';
import { parseLoteZoneId } from '../utils/lote-zone-options';
import { useLoteZoneOptions } from '../hooks/useLoteZoneOptions';
import { fetchLoteCycles } from '../api/crop.api';
import { KpiCard } from '../components/shared/KpiCard';
import { useAuthStore } from '../store/auth.store';
import { useRanchoStore } from '../store/rancho.store';
import { TelegramHelpButton } from '../components/shared/TelegramHelpButton';
import {
  fetchMermaByLote, fetchMermaKpi, fetchMermaSummary, createMermaRecord, closeMermaReport,
  type MermaRecord, type MermaReason, type CreateMermaInput,
} from '../api/merma.api';
import { PageTitle } from '../components/shared/PageTitle';
import { DataTable } from '../components/shared/DataTable';

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  kpiRow:  { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  filters: { display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'flex-end', flexWrap: 'wrap' },
  table:   { width: '100%', overflowX: 'auto' as const },
  th:      { fontWeight: tokens.fontWeightSemibold, padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, textAlign: 'left' as const },
  td:      { padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, textAlign: 'left' as const },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
});

const REASONS: MermaReason[] = ['CLIMA', 'PLAGA', 'MECANICO', 'RECHAZO_CALIDAD', 'PUDRICION', 'OTRO'];
const REASON_KEY: Record<MermaReason, keyof ReturnType<typeof useT>['merma']> = {
  CLIMA:            'reasonCLIMA',
  PLAGA:            'reasonPLAGA',
  MECANICO:         'reasonMECANICO',
  RECHAZO_CALIDAD:  'reasonRECHAZO_CALIDAD',
  PUDRICION:        'reasonPUDRICION',
  OTRO:             'reasonOTRO',
};
const STATUS_KEY: Record<MermaRecord['status'], keyof ReturnType<typeof useT>['merma']> = {
  REGISTRADA: 'statusREGISTRADA',
  VERIFICADA: 'statusVERIFICADA',
  CERRADA:    'statusCERRADA',
};

export function MermaPage() {
  const t = useT();
  const s = useStyles();
  const { formatCurrency, formatDate } = useLocaleSettings();
  const qc = useQueryClient();
  const { user } = useAuthStore();
  // Cerrar reporte es más sensible que registrar merma: excluye FIELD_WORKER (ver merma.handler.ts)
  const canClose = user?.roles.some(r => ['SUPERVISOR', 'AGRONOMIST', 'ADMIN', 'SUPER_ADMIN'].includes(r)) ?? false;

  const { activeRanchoId } = useRanchoStore();
  // Selección del árbol lote > zona > subzona ('' = ninguna).
  const [zoneId, setZoneId] = useState('');
  const [batchId, setBatchId] = useState('');

  const { data: modules = [] } = useQuery({
    queryKey: ['modules-rancho', activeRanchoId],
    queryFn:  () => fetchModules({ ranchoId: activeRanchoId }),
  });
  const sel = zoneId ? parseLoteZoneId(zoneId, modules) : null;
  const loteId = sel?.loteId ?? '';
  const moduleId = sel?.moduleId;

  const { lotes } = useRanchoLotes(true, { value: loteId, reset: () => setZoneId('') }, undefined, true);
  const zoneOptions = useLoteZoneOptions(lotes, modules);

  // Resumen cross-lote (rancho activo) — solo se pide mientras no haya un lote
  // puntual elegido, para no duplicar la llamada con la vista per-lote de abajo.
  const { data: mermaSummary, isLoading: loadingSummary } = useQuery({
    queryKey: ['mermaSummary', activeRanchoId],
    queryFn:  () => fetchMermaSummary(activeRanchoId),
    enabled:  !loteId,
  });

  // Misma fuente de ciclos que Cultivo/Riego (fenología, riego, presupuestos, actividades),
  // no solo los lotes de empaque: una merma puede registrarse antes de empacar.
  const { data: cycles = [] } = useQuery({
    queryKey: ['lote-cycles', loteId],
    queryFn:  () => fetchLoteCycles(loteId),
    enabled:  !!loteId,
  });
  const batches = cycles.map(c => ({ batchId: c.ciclo }));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [form, setForm] = useState<Partial<CreateMermaInput>>({});
  const fe = useFieldErrors();

  const { data: records = [], isLoading } = useQuery({
    queryKey: ['merma', loteId, moduleId],
    queryFn:  () => fetchMermaByLote(loteId, moduleId),
    enabled:  !!loteId,
  });

  const { data: kpi } = useQuery({
    queryKey: ['merma-kpi', loteId, batchId, moduleId],
    queryFn:  () => fetchMermaKpi(loteId, batchId || undefined, moduleId),
    enabled:  !!loteId,
  });

  const mutation = useMutation({
    mutationFn: (input: CreateMermaInput) => createMermaRecord(loteId, moduleId ? { ...input, moduleId } : input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['merma', loteId] });
      qc.invalidateQueries({ queryKey: ['merma-kpi', loteId] });
      setDialogOpen(false);
      setForm({}); fe.clear();
    },
  });

  const closeMutation = useMutation({
    mutationFn: () => closeMermaReport(loteId, batchId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['merma', loteId] });
      qc.invalidateQueries({ queryKey: ['merma-kpi', loteId] });
      setCloseConfirmOpen(false);
    },
  });

  const chartData = kpi
    ? REASONS.map(r => ({
        name: t.merma[REASON_KEY[r]],
        kg:   kpi.rejectedByReason[r] ?? 0,
      })).filter(d => d.kg > 0)
    : [];

  return (
    <div className={s.root}>
      <PageTitle>{t.merma.title}</PageTitle>

      {/* Filtros */}
      <div className={s.filters}>
        <SearchableSelect
          label={t.merma.fieldLoteZone}
          placeholder={t.common.searchLotePlaceholder}
          options={zoneOptions}
          value={zoneId}
          onChange={(id) => { setZoneId(id); setBatchId(''); }}
          noResultsText={t.common.searchNoResults}
        />
        <SearchableSelect
          label={t.merma.fieldBatch}
          placeholder={t.common.searchBatchPlaceholder}
          options={[{ id: '', label: t.merma.filterAllBatches }, ...batches.map(b => ({ id: b.batchId, label: b.batchId }))]}
          value={batchId}
          onChange={setBatchId}
          disabled={!loteId}
          noResultsText={t.common.searchNoResults}
        />
      </div>

      {/* KPIs */}
      {kpi && (
        <div className={s.kpiRow}>
          <KpiCard label={t.merma.kpiRejectedKg}    value={`${kpi.totalRejectedKg.toFixed(1)} kg`} />
          <KpiCard label={t.merma.kpiImpact}      value={formatCurrency(kpi.totalWasteImpactCost)} />
          <KpiCard label={t.merma.kpiRecords}    value={String(kpi.recordCount)} />
        </div>
      )}

      {/* Chart */}
      {chartData.length > 0 && (
        <div>
          <strong>{t.merma.chartTitle}</strong>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis unit=" kg" />
              <Tooltip />
              <Bar dataKey="kg" fill={tokens.colorBrandBackground} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Resumen cross-lote (rancho activo) — visible mientras no se elige un lote puntual */}
      {!loteId && (
        loadingSummary ? (
          <Spinner size="small" label={t.merma.loading} />
        ) : (
          <>
            <div className={s.kpiRow}>
              <KpiCard
                label={t.merma.kpiRejectedKg}
                value={`${(mermaSummary?.kpi.totalRejectedKg ?? 0).toFixed(1)} kg`}
              />
              <KpiCard
                label={t.merma.kpiImpact}
                value={formatCurrency(mermaSummary?.kpi.totalWasteImpactCost ?? 0)}
              />
              <KpiCard
                label={t.merma.kpiRecords}
                value={String(mermaSummary?.kpi.recordCount ?? 0)}
              />
            </div>

            <DataTable<NonNullable<typeof mermaSummary>['records'][number]>
              rows={mermaSummary?.records ?? []}
              rowKey={(r) => r.mermaId}
              emptyMessage={t.merma.noRecords}
              columns={[
                { key: 'date', header: t.common.date, render: (r) => formatDate(r.registeredAt) },
                { key: 'lote', header: t.merma.tableLote, render: (r) => lotes.find(l => l.loteId === r.loteId)?.name ?? r.loteId },
                { key: 'reason', header: t.merma.fieldReason, render: (r) => t.merma[REASON_KEY[r.reason]] },
                { key: 'kg', header: t.merma.fieldKg, render: (r) => (r.rejectedKg ?? 0).toFixed(1) },
                { key: 'status', header: t.common.status, render: (r) => <Badge appearance="tint">{t.merma[STATUS_KEY[r.status]]}</Badge> },
              ]}
            />
          </>
        )
      )}

      {/* Toolbar + tabla */}
      {loteId && (
        <>
          <div className={s.toolbar}>
            <strong>{t.merma.registerTitle}</strong>
            <div style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
              {canClose && (
                <Button
                  appearance="secondary"
                  disabled={!batchId}
                  onClick={() => setCloseConfirmOpen(true)}
                >
                  {t.merma.closeBtn}
                </Button>
              )}
              <Button appearance="primary" onClick={() => setDialogOpen(true)}>
                {t.merma.registerTitle}
              </Button>
            </div>
          </div>

          {isLoading && <Spinner size="small" label={t.merma.loading} />}

          <DataTable<(typeof records)[number]>
            rows={isLoading ? [] : records}
            rowKey={(r) => r.mermaId}
            emptyMessage={isLoading ? '…' : t.merma.noRecords}
            columns={[
              { key: 'reason', header: t.merma.fieldReason, render: (r) => t.merma[REASON_KEY[r.reason]] },
              { key: 'kg', header: t.merma.fieldKg, render: (r) => (r.rejectedKg ?? 0).toFixed(1) },
              { key: 'boxes', header: t.merma.fieldBoxes, render: (r) => r.rejectedBoxes ?? '—' },
              { key: 'status', header: t.common.status, render: (r) => <Badge appearance="tint">{t.merma[STATUS_KEY[r.status]]}</Badge> },
              { key: 'date', header: t.common.date, render: (r) => formatDate(r.registeredAt) },
              { key: 'notes', header: t.common.notes, render: (r) => r.notes ?? '—' },
            ]}
          />
        </>
      )}

      {/* Dialog registrar merma */}
      <Dialog open={dialogOpen} onOpenChange={(_, d) => { setDialogOpen(d.open); if (!d.open) { setForm({}); fe.clear(); } }}>
        <DialogSurface>
          <DialogTitle action={
            <TelegramHelpButton
              topic="merma"
              voiceExample={t.telegramHelp.mermaVoice}
              voiceDesc={t.telegramHelp.mermaDesc}
            />
          }>{t.merma.registerTitle}</DialogTitle>
          <DialogBody>
            <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
              <Field label={t.merma.fieldBatch} required {...fe.props('batchId')}>
                {batches.length > 0 ? (
                  <SearchableSelect
                    placeholder={t.common.searchBatchPlaceholder}
                    options={batches.map(b => ({ id: b.batchId, label: b.batchId }))}
                    value={form.batchId ?? batchId}
                    onChange={(id) => { setForm(f => ({ ...f, batchId: id })); fe.clear('batchId'); }}
                    noResultsText={t.common.searchNoResults}
                  />
                ) : (
                  <Input
                    value={form.batchId ?? batchId}
                    onChange={(_, d) => { setForm(f => ({ ...f, batchId: d.value })); fe.clear('batchId'); }}
                    placeholder={t.common.searchBatchPlaceholder}
                  />
                )}
              </Field>
              <Field label={t.merma.fieldReason} required {...fe.props('reason')}>
                <Select
                  value={form.reason ?? ''}
                  onChange={(_, d) => { setForm(f => ({ ...f, reason: d.value as MermaReason })); fe.clear('reason'); }}
                >
                  <option value="">—</option>
                  {REASONS.map(r => (
                    <option key={r} value={r}>{t.merma[REASON_KEY[r]]}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t.merma.fieldKg} required {...fe.props('rejectedKg')}>
                <NumberInput
                  value={form.rejectedKg}
                  onValueChange={(v) => { setForm(f => ({ ...f, rejectedKg: parseFloat(v) })); fe.clear('rejectedKg'); }}
                />
              </Field>
              <Field label={t.merma.fieldBoxes}>
                <NumberInput
                  value={form.rejectedBoxes}
                  onValueChange={(v) => setForm(f => ({ ...f, rejectedBoxes: parseInt(v, 10) }))}
                />
              </Field>
              <Field label={t.common.notes}>
                <Textarea
                  value={form.notes ?? ''}
                  onChange={(_, d) => setForm(f => ({ ...f, notes: d.value }))}
                />
              </Field>
            </DialogContent>
          </DialogBody>
          <DialogActions>
            <Button onClick={() => { setDialogOpen(false); setForm({}); fe.clear(); }}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              disabled={mutation.isPending}
              onClick={() => {
                const req = t.common.validation.fieldRequired;
                const ok = fe.validate({
                  batchId: !form.batchId && !batchId && req,
                  reason: !form.reason && req,
                  rejectedKg: !form.rejectedKg && req,
                });
                if (!ok) return;
                const input: CreateMermaInput = {
                  batchId:      form.batchId ?? batchId,
                  reason:       form.reason!,
                  rejectedKg:   form.rejectedKg!,
                  rejectedBoxes: form.rejectedBoxes,
                  notes:        form.notes,
                };
                mutation.mutate(input);
              }}
            >
              {t.common.save}
            </Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>

      {/* Dialog confirmar cierre de reporte */}
      <Dialog open={closeConfirmOpen} onOpenChange={(_, d) => setCloseConfirmOpen(d.open)}>
        <DialogSurface>
          <DialogTitle>{t.merma.closeBtn}</DialogTitle>
          <DialogBody>
            <DialogContent>
              <p>{t.merma.closeConfirm}</p>
            </DialogContent>
          </DialogBody>
          <DialogActions>
            <Button onClick={() => setCloseConfirmOpen(false)} disabled={closeMutation.isPending}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              disabled={closeMutation.isPending}
              icon={closeMutation.isPending ? <Spinner size="tiny" /> : undefined}
              onClick={() => closeMutation.mutate()}
            >
              {t.merma.closeBtn}
            </Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
